import { useSyncExternalStore } from 'react'

export type Breakpoint = 'md' | 'lg' | 'xl' | '2xl'

export interface UIBreakpointState {
  bp: Breakpoint
  isCompact: boolean
  isFolded: boolean
  isWide: boolean
}

const BP_MIN: Record<Breakpoint, number> = {
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
}

function resolveBp(): Breakpoint {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'lg'
  }
  if (window.matchMedia(`(min-width: ${BP_MIN['2xl']}px)`).matches) return '2xl'
  if (window.matchMedia(`(min-width: ${BP_MIN.xl}px)`).matches) return 'xl'
  if (window.matchMedia(`(min-width: ${BP_MIN.lg}px)`).matches) return 'lg'
  return 'md'
}

let warned = false

function subscribe(callback: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    if (!warned) {
      console.warn('[useUIBreakpoint] matchMedia unavailable — falling back to lg')
      warned = true
    }
    return () => {}
  }
  const queries = (Object.keys(BP_MIN) as Breakpoint[]).map((bp) =>
    window.matchMedia(`(min-width: ${BP_MIN[bp]}px)`),
  )
  queries.forEach((q) => q.addEventListener('change', callback))
  return () => queries.forEach((q) => q.removeEventListener('change', callback))
}

export function useUIBreakpoint(): UIBreakpointState {
  const bp = useSyncExternalStore(
    subscribe,
    resolveBp,
    () => 'lg' as Breakpoint, // SSR snapshot
  )
  return {
    bp,
    isCompact: bp === 'md',
    isFolded: bp === 'md' || bp === 'lg',
    isWide: bp === '2xl',
  }
}

// Exported only for tests — production code should never call this.
export function __resetMatchMediaWarningForTests(): void {
  warned = false
}
